import { spring, TransitionMotion } from '@serprex/react-motion';
import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import {
    ThemeStyles,
    DarkAbilitiesSvg,
    DarkActionsSvg,
    DarkEquipmentSvg,
    DarkSpellsSvg,
    DarkDescriptionSvg,
    DarkNotesSvg,
    DarkFeaturesSvg,
    DarkExtrasSvg,
    LightAbilitiesSvg,
    LightActionsSvg,
    LightEquipmentSvg,
    LightSpellsSvg,
    LightDescriptionSvg,
    LightNotesSvg,
    LightFeaturesSvg,
    LightExtrasSvg,
} from '@dndbeyond/character-components/es';
import { DecorationUtils, DecorationInfo, rulesEngineSelectors } from '@dndbeyond/character-rules-engine/es';

import { Swipeable } from '~/components/Swipeable';

import { sidebarActions } from '../../../Shared/actions';
import Sidebar from '../../../Shared/containers/Sidebar';
import { appEnvSelectors } from '../../../Shared/selectors';
import { AppEnvDimensionsState } from '../../../Shared/stores/typings';
import { ComponentCarousel, ComponentCarouselItem } from '../../components/ComponentCarousel';
import { QuickNav, QuickNavItem } from '../../components/QuickNav';
import SectionPlaceholder from '../../components/SectionPlaceholder';
import { sheetAppSelectors } from '../../selectors';
import { SheetAppState, SidebarInfo, SidebarPositioningInfo } from '../../typings';
import BackdropStyles from '../BackdropStyles';
import CharacterHeaderTablet from '../CharacterHeaderTablet';
import ActionsTablet from '../tablet/ActionsTablet';
import CombatTablet from '../tablet/CombatTablet';
import DescriptionTablet from '../tablet/DescriptionTablet';
import EquipmentTablet from '../tablet/EquipmentTablet';
import ExtrasTablet from '../tablet/ExtrasTablet';
import FeaturesTablet from '../tablet/FeaturesTablet';
import MainTablet from '../tablet/MainTablet';
import NotesTablet from '../tablet/NotesTablet';
import SpellsTablet from '../tablet/SpellsTablet';

const SECTION = {
    __FILLER__: '__FILLER__',
    ACTIONS: 'ACTIONS',
    SKILLS: 'SKILLS',
    MAIN: 'MAIN',
    PROFICIENCIES_LANGUAGES: 'PROFICIENCIES_LANGUAGES',
    FEATURES_TRAITS: 'FEATURES_TRAITS',
    EQUIPMENT: 'EQUIPMENT',
    SPELLS: 'SPELLS',
    NOTES: 'NOTES',
    DESCRIPTION: 'DESCRIPTION',
    EXTRAS: 'EXTRAS',
};

interface Props extends DispatchProp {
    sidebarPositioning: SidebarPositioningInfo;
    sidebarInfo: SidebarInfo;
    envDimensions: AppEnvDimensionsState;
    decorationInfo: DecorationInfo;
    hasSpells: boolean;
    isReadonly: boolean;
}
interface State {
    activeSectionId: string;
    swipedAmount: number;
}
class CharacterSheetTablet extends React.PureComponent<Props, State> {
    constructor(props) {
        super(props);

        this.state = {
            activeSectionId: SECTION.MAIN,
            swipedAmount: 0,
        };
    }

    handleComponentChange = (id: string): void => {
        this.setState({
            activeSectionId: id,
        });
        window.scrollTo(0, 0);
    };

    handleItemChange = (newKey: string, oldKey: string): void => {
        if (newKey !== oldKey) {
            window.scrollTo(0, 0);
        }

        this.setState({
            activeSectionId: newKey,
        });
    };

    handleSidebarClick = (): void => {
        const { dispatch, sidebarInfo } = this.props;

        dispatch(sidebarActions.visibleSet(!sidebarInfo.isVisible));
    };

    getPositionX = (swipedAmount: number): number => {
        const { sidebarInfo } = this.props;

        let position: number = (sidebarInfo.isVisible ? 0 : sidebarInfo.width) + swipedAmount;

        if (swipedAmount > 0) {
            return Math.min(sidebarInfo.width, position);
        } else {
            return Math.max(0, position);
        }
    };

    renderSections = (): React.ReactNode => {
        const { activeSectionId } = this.state;
        const { envDimensions, hasSpells, isReadonly, decorationInfo } = this.props;

        const isDarkMode = DecorationUtils.isDarkMode(decorationInfo);

        return (
            <ComponentCarousel
                activeItemKey={activeSectionId}
                onItemChange={this.handleItemChange}
                envDimensions={envDimensions}
                changingWaitTime={750}
            >
                <ComponentCarouselItem
                    itemKey={SECTION.MAIN}
                    PlaceholderComponent={SectionPlaceholder}
                    placeholderProps={{
                        name: 'Abilities, Saves, Senses',
                        IconComponent: isDarkMode ? LightAbilitiesSvg : DarkAbilitiesSvg,
                    }}
                    ContentComponent={MainTablet}
                />
                <ComponentCarouselItem
                    itemKey={SECTION.ACTIONS}
                    PlaceholderComponent={SectionPlaceholder}
                    placeholderProps={{
                        name: 'Actions',
                        IconComponent: isDarkMode ? LightActionsSvg : DarkActionsSvg,
                    }}
                    ContentComponent={ActionsTablet}
                />
                <ComponentCarouselItem
                    itemKey={SECTION.EQUIPMENT}
                    PlaceholderComponent={SectionPlaceholder}
                    placeholderProps={{
                        name: 'Equipment',
                        IconComponent: isDarkMode ? LightEquipmentSvg : DarkEquipmentSvg,
                    }}
                    ContentComponent={EquipmentTablet}
                />
                <ComponentCarouselItem
                    itemKey={SECTION.SPELLS}
                    PlaceholderComponent={SectionPlaceholder}
                    placeholderProps={{
                        name: 'Spells',
                        IconComponent: isDarkMode ? LightSpellsSvg : DarkSpellsSvg,
                    }}
                    ContentComponent={SpellsTablet}
                    isEnabled={hasSpells}
                />
                <ComponentCarouselItem
                    itemKey={SECTION.FEATURES_TRAITS}
                    PlaceholderComponent={SectionPlaceholder}
                    placeholderProps={{
                        name: 'Features & Traits',
                        IconComponent: isDarkMode ? LightFeaturesSvg : DarkFeaturesSvg,
                    }}
                    ContentComponent={FeaturesTablet}
                />
                <ComponentCarouselItem
                    itemKey={SECTION.DESCRIPTION}
                    PlaceholderComponent={SectionPlaceholder}
                    placeholderProps={{
                        name: 'Description',
                        IconComponent: isDarkMode ? LightDescriptionSvg : DarkDescriptionSvg,
                    }}
                    ContentComponent={DescriptionTablet}
                    isEnabled={!isReadonly}
                />
                <ComponentCarouselItem
                    itemKey={SECTION.NOTES}
                    PlaceholderComponent={SectionPlaceholder}
                    placeholderProps={{
                        name: 'Notes',
                        IconComponent: isDarkMode ? LightNotesSvg : DarkNotesSvg,
                    }}
                    ContentComponent={NotesTablet}
                    isEnabled={!isReadonly}
                />
                <ComponentCarouselItem
                    itemKey={SECTION.EXTRAS}
                    PlaceholderComponent={SectionPlaceholder}
                    placeholderProps={{
                        name: 'Extras',
                        IconComponent: isDarkMode ? LightExtrasSvg : DarkExtrasSvg,
                    }}
                    ContentComponent={ExtrasTablet}
                    isEnabled={!isReadonly}
                />
            </ComponentCarousel>
        );
    };

    renderQuickNav = (): React.ReactNode => {
        const { hasSpells, isReadonly, sidebarInfo } = this.props;

        return (
            <QuickNav
                onChange={this.handleComponentChange}
                onSidebarClick={this.handleSidebarClick}
                sidebarInfo={sidebarInfo}
            >
                <QuickNavItem id={SECTION.MAIN} cssKey="main" IconComponent={LightAbilitiesSvg} columns={2}>
                    Abilities, Saves, Senses, Proficiencies, Languages, Skills
                </QuickNavItem>
                <QuickNavItem id={SECTION.ACTIONS} cssKey="actions" IconComponent={LightActionsSvg}>
                    Actions
                </QuickNavItem>
                <QuickNavItem id={SECTION.EQUIPMENT} cssKey="equipment" IconComponent={LightEquipmentSvg}>
                    Inventory
                </QuickNavItem>
                <QuickNavItem id={SECTION.SPELLS} cssKey="spells" isEnabled={hasSpells} IconComponent={LightSpellsSvg}>
                    Spells
                </QuickNavItem>
                <QuickNavItem id={SECTION.FEATURES_TRAITS} cssKey="features" IconComponent={LightFeaturesSvg}>
                    Features &amp; Traits
                </QuickNavItem>
                <QuickNavItem
                    id={SECTION.DESCRIPTION}
                    cssKey="description"
                    isEnabled={!isReadonly}
                    IconComponent={LightDescriptionSvg}
                >
                    Description
                </QuickNavItem>
                <QuickNavItem id={SECTION.NOTES} cssKey="notes" isEnabled={!isReadonly} IconComponent={LightNotesSvg}>
                    Notes
                </QuickNavItem>
                <QuickNavItem
                    id={SECTION.EXTRAS}
                    cssKey="extras"
                    isEnabled={!isReadonly}
                    IconComponent={LightExtrasSvg}
                    columns={2}
                >
                    Extras
                </QuickNavItem>
            </QuickNav>
        );
    };

    renderSidebar = (): React.ReactNode => {
        const { swipedAmount } = this.state;
        const { sidebarPositioning } = this.props;

        return (
            <TransitionMotion
                styles={[
                    {
                        key: '1',
                        style: {
                            transform: spring(this.getPositionX(swipedAmount), { stiffness: 400, damping: 30 }),
                        },
                    },
                ]}
            >
                {(interpolatedStyles) => (
                    <React.Fragment>
                        {interpolatedStyles.map((config) => (
                            <Sidebar
                                key={config.key}
                                style={{
                                    ...sidebarPositioning,
                                    transform: `translateX(${config.style.transform}px)`,
                                }}
                                setSwipedAmount={(swipedAmount) => this.setState({ swipedAmount })}
                            />
                        ))}
                    </React.Fragment>
                )}
            </TransitionMotion>
        );
    };

    render() {
        const { decorationInfo } = this.props;

        return (
            <div className="ct-character-sheet-tablet">
                <CharacterHeaderTablet />
                <CombatTablet />
                {this.renderSections()}
                {this.renderQuickNav()}
                {this.renderSidebar()}
                <ThemeStyles decorationInfo={decorationInfo} />
                <BackdropStyles backdrop={DecorationUtils.getBackdropInfo(decorationInfo)} />
            </div>
        );
    }
}

function mapStateToProps(state: SheetAppState) {
    return {
        sidebarPositioning: sheetAppSelectors.getSidebarPositioning(state),
        sidebarInfo: sheetAppSelectors.getSidebarInfo(state),
        decorationInfo: rulesEngineSelectors.getDecorationInfo(state),
        hasSpells: rulesEngineSelectors.hasSpells(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
        envDimensions: appEnvSelectors.getDimensions(state),
    };
}

export default connect(mapStateToProps)(CharacterSheetTablet);
